import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, ApiError, setToken, getToken } from '../api/client';
import type { Utilisateur } from '../api/types';

interface Ctx {
  user: Utilisateur | null;
  chargement: boolean;
  /** Le jeton est valide mais le serveur n'a pas repondu au demarrage. */
  serveurInjoignable: boolean;
  reessayer: () => void;
  connexion: (email: string, motDePasse: string) => Promise<void>;
  deconnexion: () => void;
  peut: (capacite: string) => boolean;
  estAgent: boolean;
}
const AuthCtx = createContext<Ctx | null>(null);

/** Liste de référence, copiée de server/src/lib/permissions.ts. */
const CAPACITES_CONNUES = new Set([
  'voir.planning', 'voir.flotte', 'voir.clients', 'voir.reservations',
  'voir.contrats', 'voir.caisse', 'voir.analytique', 'voir.journal',
  'creer.reservation', 'creer.contrat', 'creer.etatDesLieux',
  'creer.mouvementCaisse', 'creer.client', 'creer.vehicule',
  'modifier.reservation', 'modifier.client', 'modifier.vehicule', 'modifier.etatDesLieux',
  'modifier.finance', 'gerer.taux', 'gerer.supplements',
  'cloturer.caisse', 'accorder.derogation', 'traiter.correction',
  'annuler.reservation', 'resilier.contrat', 'supprimer',
  'gerer.utilisateurs', 'gerer.parametres',
]);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<Utilisateur | null>(null);
  const [chargement, setChargement] = useState(true);
  const [serveurInjoignable, setServeurInjoignable] = useState(false);
  const [essai, setEssai] = useState(0);

  useEffect(() => {
    if (!getToken()) { setChargement(false); return; }
    setChargement(true); setServeurInjoignable(false);
    api.get<Utilisateur>('/auth/moi')
      .then((u) => { setUser(u); setServeurInjoignable(false); })
      // Une coupure de reseau n'est PAS une session invalide. Effacer le jeton
      // sur un `TypeError: Failed to fetch` deconnectait l'agent au premier
      // creux de couverture, sur le parking, au milieu d'un etat des lieux.
      // Et retomber sur l'ecran de connexion est indiscernable d'une
      // deconnexion : l'agent retape son mot de passe pour rien.
      .catch((e) => {
        if (e instanceof ApiError && e.status === 401) { setToken(null); return; }
        setServeurInjoignable(true);
      })
      .finally(() => setChargement(false));
  }, [essai]);

  const reessayer = useCallback(() => setEssai((n) => n + 1), []);

  const connexion = useCallback(async (email: string, motDePasse: string) => {
    const r = await api.post<{ token: string; utilisateur: Utilisateur }>('/auth/connexion', { email, motDePasse });
    setToken(r.token);
    setUser(r.utilisateur);
  }, []);

  const deconnexion = useCallback(() => { setToken(null); setUser(null); }, []);

  const valeur = useMemo<Ctx>(() => ({
    user, chargement, serveurInjoignable, reessayer, connexion, deconnexion,
    peut: (c) => {
      // Garde-fou : une clé de droit est une valeur TECHNIQUE échangée avec le
      // serveur. Si elle est mal orthographiée — un accent ajouté par une passe
      // de traduction, par exemple — la comparaison échoue en silence et le
      // bouton disparaît pour tout le monde, sans le moindre message.
      // C'est arrivé. On ne veut pas que ça se reproduise sans bruit.
      if (import.meta.env.DEV && user && !CAPACITES_CONNUES.has(c)) {
        // eslint-disable-next-line no-console
        console.error(
          `[droits] capacité inconnue « ${c} » — vérifiez l'orthographe exacte ` +
          `(sans accent). Le bouton correspondant restera invisible.`,
        );
      }
      return user?.capacites.includes(c) ?? false;
    },
    estAgent: user?.role === 'agent',
  }), [user, chargement, serveurInjoignable, reessayer, connexion, deconnexion]);

  return <AuthCtx.Provider value={valeur}>{children}</AuthCtx.Provider>;
}

export function useAuth(): Ctx {
  const c = useContext(AuthCtx);
  if (!c) throw new Error('useAuth doit être utilise dans AuthProvider');
  return c;
}
